/**
 * Shared state for the AI prompt debug drawer. Inert in production: the provider
 * renders children unwrapped and `useAiDebug` hands back a stable no-op, so
 * callers never need to guard the environment themselves.
 */
"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import AiPromptDebugDrawer, {
  type DebugMessage,
} from "@/components/molecules/debug/AiPromptDebugDrawer";

interface AiDebugState {
  systemPrompt: string;
  messages: DebugMessage[];
  modelId: string;
  structuredOutput: Record<string, unknown> | null;
  debugInfo: Record<string, unknown>;
}

interface AiDebugContextValue {
  updateDebugState: (state: Partial<AiDebugState>) => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const AiDebugContext = createContext<AiDebugContextValue | null>(null);

/** Stable no-op implementation for when context is not available */
const noopContext: AiDebugContextValue = {
  updateDebugState: () => {},
  isOpen: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
};

export function useAiDebug() {
  const context = useContext(AiDebugContext);
  // Return stable no-op when not wrapped in provider (e.g., production)
  return context ?? noopContext;
}

interface AiDebugProviderProps {
  children: ReactNode;
}

export function AiDebugProvider({ children }: AiDebugProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<AiDebugState>({
    systemPrompt: "",
    messages: [],
    modelId: "",
    structuredOutput: null,
    debugInfo: {},
  });

  // Keyboard shortcut: Ctrl+Shift+D
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const updateDebugState = useCallback((partial: Partial<AiDebugState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const contextValue = useMemo(
    () => ({ updateDebugState, isOpen, open, close, toggle }),
    [updateDebugState, isOpen, open, close, toggle],
  );

  // Production: render children without the provider or drawer.
  if (process.env.NODE_ENV === "production") {
    return <>{children}</>;
  }

  return (
    <AiDebugContext.Provider value={contextValue}>
      {children}
      <AiPromptDebugDrawer
        isOpen={isOpen}
        onClose={close}
        systemPrompt={state.systemPrompt}
        messages={state.messages}
        modelId={state.modelId}
        structuredOutput={state.structuredOutput}
        debugInfo={state.debugInfo}
      />
    </AiDebugContext.Provider>
  );
}
